/* eslint-disable no-unused-vars */
import {ConnectionStrategy} from '../connection/ConnectionStrategy'
import {isNeoLineNep21Provider} from '../connection/NeoLineConnection'
import {isNeolineExtensionProvider} from '../connection/NeolineExtensionConnection'
import {isNeolineNeoXEthereumProvider} from '../connection/NeolineNeoXEthereumConnection'
import {isOneGateNep21Provider} from '../connection/OneGateConnection'
import {isMetaMaskProvider} from '../connection/MetaMaskConnection'
import {isWalletConnectProvider} from '../connection/WalletConnectContext'
import {WalletProviderStrategy} from '../providers/WalletProviderStrategy'
import {NetworkStrategy} from '../networks/NetworkStrategy'

export class WalletAdapter {
  private provider: any = null

  constructor(
    private connection: ConnectionStrategy,
    private wallet: WalletProviderStrategy,
    private network: NetworkStrategy
  ) {}

  get platform() {
    return this.wallet.platform
  }

  // Starting the connection must not read the wallet's account: wallets such as
  // NeoLine answer an account query with their own approval dialog, and nothing
  // asked to connect yet. The stored-session restore drives that instead.
  async init(onDeleteAdapter?: () => void) {
    await this.connection.init?.(onDeleteAdapter)
  }

  async restoreSession() {
    await this.connection.restoreSession?.()
    this.syncProviderFromConnection()
  }

  restoreBlockedByNetwork() {
    return this.connection.restoreBlockedByNetwork?.() ?? false
  }

  private syncProviderFromConnection() {
    const restoredProvider = this.connection.getConnectedProvider?.()

    if (restoredProvider) {
      this.provider = restoredProvider
    }
  }

  private resolveProvider() {
    return this.provider ?? this.connection.getConnectedProvider?.() ?? null
  }

  isInstalled() {
    return this.connection.isInstalled(this.wallet.nep21Name)
  }

  async connect() {
    const result = await this.connection.connect(this.wallet.nep21Name)

    if (result?.approval) {
      const originalApproval = result.approval

      return {
        uri: result.uri ?? null,
        approval: async () => {
          await originalApproval()
          this.provider = this.connection.getConnectedProvider?.() ?? null
        },
      }
    }

    this.provider = result

    return {
      uri: null,
      approval: async () => {},
    }
  }

  async disconnect() {
    await this.connection.disconnect?.()

    this.provider = null
  }

  getAddress() {
    const provider = this.resolveProvider()

    if (!provider) return null

    return this.wallet.getAddress(provider)
  }

  async invoke(params: any) {
    const provider = this.resolveProvider()

    if (!provider) throw new Error('Wallet not connected')

    await this.connection.ensureNetwork?.()

    const passthroughPayload =
      isWalletConnectProvider(provider) ||
      isNeolineExtensionProvider(provider) ||
      isNeoLineNep21Provider(provider) ||
      isNeolineNeoXEthereumProvider(provider) ||
      isMetaMaskProvider(provider) ||
      isOneGateNep21Provider(provider)

    const payload = passthroughPayload
      ? params
      : this.network.buildPayload(params)

    return this.wallet.invoke(provider, payload)
  }
}
